import { useQuery } from '@tanstack/react-query';

import { almacen } from '../api';
import { descargarCsv } from '../csv';
import { diaDeLima } from '../formato';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Pastilla,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, contados, soles } from '../formato';
import { mayuscula, useVocabulario } from '../vocabulario';

/**
 * Dónde está la mercadería de la cadena, y dónde falta.
 *
 * El stock es del local y eso no cambia. Lo que cambia es que el dueño de tres
 * locales tenía que entrar tres veces para saber dónde estaba lo suyo, y cuando
 * faltaba pulpo en Miraflores y sobraba en San Isidro **nadie lo veía**: el
 * local que se quedó sin pulpo lo compraba otra vez mientras el otro lo tiraba
 * por vencido.
 *
 * Es la vuelta al traspaso. Existía la operación que mueve mercadería entre
 * locales y no existía lo que dice cuándo hacerla.
 */
export function Cadena() {
  const vocabulario = useVocabulario();
  const consulta = useQuery({
    queryKey: ['cadena'],
    queryFn: almacen.chain,
  });

  const datos = consulta.data;
  const locales = datos?.branches ?? [];
  const sugerencias = datos?.suggestions ?? [];

  return (
    <>
      <Cabecera
        titulo="La cadena"
        subtitulo="Dónde está la mercadería y dónde hace falta" ayuda="El stock de todos los locales juntos. Sugiere mandar de donde sobra a donde falta, antes de comprar otra vez."
        acciones={
          <Boton
            onClick={() =>
              descargarCsv(
                `cadena-${diaDeLima()}.csv`,
                ['Código', mayuscula(vocabulario.insumo), 'Unidad', ...locales.map((local) => local.name), 'Total'],
                (datos?.supplies ?? []).map((insumo) => [
                  insumo.sku,
                  insumo.name,
                  insumo.unit,
                  // Vacío es «aquí no se usa», que no es cero.
                  ...locales.map((local) => insumo.byBranch[local.id]?.quantity ?? null),
                  insumo.total,
                ]),
              )
            }
            disabled={locales.length <= 1}
          >
            Exportar
          </Boton>
        }
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        {consulta.isLoading ? (
          <Cargando que="la cadena" />
        ) : locales.length <= 1 ? (
          <Vacio
            titulo={`Este ${vocabulario.negocio} tiene un solo local`}
            mensaje="Esta pantalla compara el stock entre locales y sugiere traspasos. Con uno solo no hay nada que comparar; aparecerá con el segundo."
          />
        ) : (
          <>
            <div className="cifras">
              {locales.map((local) => (
                <Tarjeta key={local.id}>
                  <p className="versal">{local.name}</p>
                  <p className="cifra__valor">{soles(local.value)}</p>
                  <p className="cifra__apoyo">en el estante ahora</p>
                </Tarjeta>
              ))}
            </div>

            {sugerencias.length > 0 ? (
              <Tarjeta>
                <h2 className="tarjeta__titulo">
                  Traspasos que ahorran comprar{' '}
                  <span className="mono apagado">({sugerencias.length})</span>
                </h2>
                <p className="nota">
                  {/*
                   * El número que decide: lo que el local que recibe no tendrá
                   * que comprar. Es lo que hace que valga la pena mandar a
                   * alguien de un local al otro, o no.
                   */}
                  Cada línea es mercadería que falta en un local y sobra en
                  otro. El que da nunca baja de su propio mínimo: mandarle el
                  problema al vecino no lo resuelve.
                </p>
                <ul className="lista">
                  {sugerencias.map((sugerencia, indice) => (
                    <li
                      key={`${sugerencia.supplyId}-${indice}`}
                      className="lista__fila"
                    >
                      <div className="lista__texto">
                        <strong>
                          {cantidad(sugerencia.quantity)} {sugerencia.unit} de{' '}
                          {sugerencia.supply}
                        </strong>
                        <span className="apagado">
                          {sugerencia.fromName} → {sugerencia.toName}
                          {Number.parseFloat(sugerencia.stillMissing) > 0
                            ? ` · le seguirían faltando ${cantidad(sugerencia.stillMissing)} ${sugerencia.unit}`
                            : ''}
                        </span>
                      </div>
                      <Pastilla
                        texto={`Ahorra ${soles(sugerencia.savedValue)}`}
                        tono="exito"
                      />
                    </li>
                  ))}
                </ul>
              </Tarjeta>
            ) : (
              <Tarjeta>
                <h2 className="tarjeta__titulo">Nada que mover</h2>
                <p className="nota nota--pegada">
                  Ningún local tiene por debajo de su mínimo algo que a otro le
                  sobre. Si algún local está corto de todo, lo que hace falta es
                  comprar y no traspasar: eso está en Qué comprar.
                </p>
              </Tarjeta>
            )}

            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>{mayuscula(vocabulario.insumo)}</th>
                    {locales.map((local) => (
                      <th key={local.id} className="numero">
                        {local.name}
                      </th>
                    ))}
                    <th className="numero"><ConAyuda ayuda="La suma de todos los locales.">Total</ConAyuda></th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.supplies ?? []).map((insumo) => (
                    <tr key={insumo.supplyId}>
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{insumo.name}</strong>
                          <span className="mono apagado">{insumo.sku}</span>
                        </div>
                      </td>
                      {locales.map((local) => {
                        const celda = insumo.byBranch[local.id];

                        return (
                          <td
                            key={local.id}
                            className={`numero mono ${
                              celda?.short === true ? 'negativo' : 'apagado'
                            }`}
                          >
                            {/*
                             * Un local que nunca tuvo ese insumo se deja en
                             * blanco, no en cero: cero significa «se acabó» y
                             * en blanco «aquí no se usa», que son dos cosas
                             * distintas y llevan a acciones distintas.
                             */}
                            {celda === undefined
                              ? '·'
                              : cantidad(celda.quantity)}
                          </td>
                        );
                      })}
                      <td className="numero mono">
                        <strong>{cantidad(insumo.total)}</strong>{' '}
                        {insumo.unit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="nota nota--pegada">
              En rojo, lo que está en su mínimo o por debajo en ese local.{' '}
              {contados(locales.length, 'local', 'locales')} ·{' '}
              {contados(datos?.supplies.length ?? 0, vocabulario.insumo, vocabulario.insumos)} con
              stock en alguno.
            </p>
          </>
        )}
      </div>
    </>
  );
}
